/**
 * Messages API hook — Phase 9.
 *
 * Admin-only view of the transactional email message log.
 * GET /v1/messages — list with optional status filter.
 * GET /v1/messages/{id} — single message (includes HTML body).
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@core/api/client';

// ── Types ────────────────────────────────────────────────────────────────────

export interface MessageLog {
  message_id: string;
  to_email: string;
  subject: string;
  template_name: string;
  status: 'dev_mock' | 'sent' | 'failed' | string;
  error: string | null;
  sent_at: string; // ISO 8601
  tenant_id: string;
}

export interface MessageListResponse {
  items: MessageLog[];
  total: number;
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const MESSAGES_QUERY_KEY = (status?: string) =>
  ['messages', { status }] as const;

// ── Hooks ────────────────────────────────────────────────────��───────────────

export interface UseMessagesOptions {
  status?: string;
  limit?: number;
}

export function useMessages({ status, limit = 50 }: UseMessagesOptions = {}) {
  return useQuery<MessageListResponse>({
    queryKey: MESSAGES_QUERY_KEY(status),
    queryFn: () =>
      api.get<MessageListResponse>('/v1/messages', {
        query: { status, limit },
      }),
    staleTime: 10_000,
  });
}

